<script lang="ts" setup>
import { type UpdateProjectInput } from '@/gql/graphql';
import { ProjectStatus2 } from '@/types';
import { isNotNull } from '@/utils/type';
import { graphql } from '@/gql';
import { computed, ref, onMounted } from 'vue';
import {
    RouterView,
    RouterLink,
    useRouter,
    useRoute,
    onBeforeRouteLeave,
} from 'vue-router';
import { FormName } from '@/composables/form-session';
import { useI18n } from 'vue-i18n';
import { useMutation, useQuery } from '@vue/apollo-composable';
import { useUserSettingsStore } from '@/stores/user-settings';
import { useFormSession } from '@/composables/form-session';
import ProjectStatusMenu from '@/components/ProjectStatusMenu.vue';
import AppViewHeader from '@/components/AppViewHeader.vue';
import {
    VContainer,
    VRow,
    VProgressLinear,
    VCol,
    VChipGroup,
    VTabs,
    VTab,
    VBtn,
    VIcon,
} from 'vuetify/components';
import { RouteName } from '@/router';
import ProjectEditForm from '@/components/ProjectEditForm.vue';
import { useNotificationStore } from '@/stores/notification';
import { useRouteParams, useRouteQuery } from '@vueuse/router';
import { useBreadcrumbsStore } from '@/stores/breadcrumbs';

type ProjectTab =
    | 'description'
    | 'deliverables'
    | 'subprojects'
    | 'highlights'
    | 'documents';

type TabRoute = {
    name: ProjectTab;
    route: RouteName;
};

const { clearFormSave } = useFormSession();
const router = useRouter();
const route = useRoute();

const isSubproject = computed(() => Boolean(project.value?.parentProjectId));

const tabs = computed(() => {
    const tabRoutes: TabRoute[] = [
        {
            name: 'description',
            route: RouteName['dashboard.project'],
        },
        {
            name: 'deliverables',
            route: RouteName['dashboard.project.deliverables'],
        },
        {
            name: 'highlights',
            route: RouteName['dashboard.project.highlights'],
        },
        {
            name: 'documents',
            route: RouteName['dashboard.project.downloads'],
        },
    ];

    if (!isSubproject.value) {
        tabRoutes.splice(2, 0, {
            name: 'subprojects',
            route: RouteName['dashboard.project.subprojects'],
        });
    }

    return tabRoutes;
});
const { t } = useI18n({
    inheritLocale: true,
    messages: {
        nl: {
            highlights: 'Highlights & @:previews',
            downloads: '@:documents',
            update_success: '@:project successvol bijgewerkt',
            current_portfolio: 'Huidig @.lower:portfolio',
            unsaved_changes:
                'Je hebt onopgeslagen wijzigingen. Weet je zeker dat je deze pagina wilt verlaten?',
        },
        en: {
            highlights: 'Highlights & @:previews',
            downloads: '@:documents',
            update_success: '@:project successfully updated',
            current_portfolio: 'Current @.lower:portfolio',
            unsaved_changes:
                'You have unsaved changes. Are you sure you want to leave this page?',
        },
    },
});

const projectId = useRouteParams<string>('id');
const userSettingsStore = useUserSettingsStore();
const notificationStore = useNotificationStore();
const breadcrumbsStore = useBreadcrumbsStore();

const projectVariables = computed(() => ({
    projectId: projectId.value,
    portfolioId: userSettingsStore.activePortfolio?.id,
}));

const parentProjectVariables = computed(() => ({
    filters: [
        {
            field: 'id',
            value: projectId.value,
        },
    ],
}));

const {
    result,
    loading: loadingProject,
    onResult,
} = useQuery(
    graphql(`
        query SingleProject($projectId: ID!, $portfolioId: ID!) {
            project(projectId: $projectId, portfolioId: $portfolioId)
        }
    `),
    projectVariables
);

onResult((r) => {
    breadcrumbsStore.addProject({
        id: r.data.project.id,
        name: r.data.project.name,
    });
});

const { result: parentProjectResult, loading: loadingParentProject } = useQuery(
    graphql(`
        query ParentProject($filters: [FilterCriterion]) {
            projects(filters: $filters) {
                items {
                    parentProject {
                        id
                        name
                    }
                }
            }
        }
    `),
    parentProjectVariables
);

const project = computed(() => result.value?.project);
const parentProject = computed(
    () => parentProjectResult.value?.projects?.items[0].parentProject ?? {}
);
const tags = computed(() => project.value?.tagValues.filter(isNotNull));
const status = computed(() => project.value?.status);
const focusId = computed(() => project.value?.focus?.id || '');
const needId = computed(() => project.value?.need?.id || '');

const status2 = computed<ProjectStatus2>(() => {
    if (project.value?.finished) return ProjectStatus2.FINISHED;
    else if (project.value?.onHold) return ProjectStatus2.ON_HOLD;
    return ProjectStatus2.ACTIVE;
});

const isSubmitting = ref(false);
const isEditing = ref(false);

const form = ref<InstanceType<typeof ProjectEditForm>>();
const isValid = computed(() => !!form.value?.isValid);
const isDirtyForm = computed(() => !!form.value?.isDirtyForm);

const onSubmit = async (values: UpdateProjectInput) => {
    try {
        await updateProject({ input: values });

        isEditing.value = false;
        router.replace({ path: route.path }); // Remove possible 'form_name' query
        notificationStore.add({
            type: 'success',
            message: t('update_success'),
        });
    } catch {
        notificationStore.add({ type: 'error', message: t('update_error') });
    } finally {
        isSubmitting.value = false;
    }
};

const { mutate: updateProject } = useMutation(
    graphql(`
        mutation UpdateProject($input: UpdateProjectInput!) {
            updateProject(input: $input) {
                result
            }
        }
    `),
    {
        refetchQueries: ['Project', 'ProjectDetails'],
        awaitRefetchQueries: true,
    }
);

const formQuery = useRouteQuery<FormName>('form');

onMounted(() => {
    if (formQuery.value && formQuery.value === FormName.project_edit) {
        isEditing.value = true;
    }
});

function handleCancelEdit() {
    clearFormSave();

    isEditing.value = false;
    isSubmitting.value = false;

    // Clear the form query
    router.push({
        path: route.path,
        query: {},
    });
}

onBeforeRouteLeave((to) => {
    // Skip confirmation when being logged out
    if (to.name === RouteName['account.login']) {
        return true;
    }

    if (isDirtyForm.value) {
        const confirmLeave = window.confirm(t('unsaved_changes'));
        if (!confirmLeave) {
            return false;
        }
    }
});
</script>

<template>
    <AppViewHeader :is-editing :class="{ subproject: isSubproject }">
        <v-container class="pb-6">
            <v-row class="d-flex">
                <v-col
                    v-if="!loadingProject && !loadingParentProject && project"
                    md="8"
                    class="d-flex flex-column pb-0"
                >
                    <router-link
                        v-if="isSubproject"
                        class="parent-project-name text-title-medium mb-2"
                        :to="{
                            name: RouteName['dashboard.project'],
                            params: {
                                id: project.parentProjectId,
                            },
                        }"
                    >
                        {{ parentProject.name }}</router-link
                    >
                    <h1 class="text-h3" data-name="project-name">
                        <v-icon
                            v-if="isSubproject"
                            icon="$subdirectory"
                            size="24"
                        />
                        {{ project.name }}
                    </h1>
                </v-col>
                <v-col
                    v-if="!loadingProject && project && isEditing"
                    class="d-flex align-end"
                >
                    <div v-if="isEditing">
                        <v-btn
                            variant="outlined"
                            class="mr-5"
                            @click="handleCancelEdit"
                        >
                            {{ t('cancel') }}
                        </v-btn>
                        <v-btn
                            :disabled="!isValid || !isDirtyForm"
                            @click="isSubmitting = true"
                        >
                            {{ t('save') }}
                        </v-btn>
                    </div>
                </v-col>
                <template v-if="!isEditing">
                    <v-col
                        sm="12"
                        md="6"
                        class="d-flex pt-0 flex-column flex-wrap"
                    >
                        <v-progress-linear
                            v-if="loadingProject"
                            color="neutral"
                            type="text"
                        ></v-progress-linear>
                        <template v-else-if="project">
                            <div v-if="!isEditing">
                                <span
                                    class="text-label-large"
                                    data-name="current-portfolio"
                                >
                                    {{ t('current_portfolio') }}:
                                    {{
                                        userSettingsStore.activePortfolio?.name
                                    }}
                                </span>
                                <v-chip-group
                                    v-if="project?.tagValues?.length"
                                    class="pb-0"
                                    column
                                >
                                    <v-tag
                                        v-for="tag in tags"
                                        :key="tag.id"
                                        variant="outlined"
                                        >{{ tag.name }}</v-tag
                                    >
                                </v-chip-group>
                            </div>
                        </template>
                    </v-col>
                    <v-col
                        sm="12"
                        md="6"
                        class="d-flex flex-row align-end justify-end ga-3"
                    >
                        <ProjectStatusMenu
                            v-if="!isEditing && project?.status"
                            :status
                            :status2
                            :loading="loadingProject"
                            :project
                            :focus-id
                            :need-id
                            :is-subproject
                            @edit="isEditing = true"
                        />
                    </v-col>
                </template>
            </v-row>
        </v-container>
    </AppViewHeader>
    <v-tabs v-if="!isEditing" data-name="project-navigation">
        <v-container>
            <v-tab
                v-for="tab in tabs"
                :key="tab.name"
                :to="{ name: tab.route, params: { id: project?.id } }"
                hide-slider
            >
                {{ t(tab.name) }}
            </v-tab>
        </v-container>
    </v-tabs>
    <div class="project-content">
        <router-view v-if="!isEditing" />
        <v-container v-else class="pt-6">
            <ProjectEditForm ref="form" :is-submitting @submit="onSubmit" />
        </v-container>
    </div>
</template>

<style lang="scss" scoped>
.project-content {
    background-color: rgb(var(--v-theme-neutral-99));
    padding-bottom: 1.5rem;
}

.subproject {
    border-top: 1px solid rgb(var(--v-theme-primary-40));
    border-top-style: dashed;
}

.parent-project-name {
    color: rgb(var(--v-theme-primary-70));
    text-decoration: none;
}
</style>
